/**
 * EngagementBidIntelligence
 *
 * Changes from previous version:
 *   - Added year filter pills (defaults to 'all')
 *   - filteredComponents derived from year selection
 *   - forwardRef exposes getFilteredComponents() so Step3SelectTemplate
 *     can retrieve the filtered set at "Use as Template" click time
 */

import React, { useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import {
  BanknotesIcon, ClockIcon, ChartBarIcon, UserGroupIcon, CalendarIcon,
  ArrowTrendingUpIcon, ArrowTrendingDownIcon, TableCellsIcon, CalculatorIcon,
} from '@heroicons/react/24/outline';
import { useBidEngagementComponents } from '../../../hooks/bid_wizard/useBidEngagements';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (val) =>
  val != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val)
    : '—';
const fmtHours = (val) => val != null ? `${Number(val).toFixed(1)} hrs` : '—';
const fmtPct   = (val) => val != null ? `${Number(val).toFixed(1)}%`   : '—';
const variance = (actual, proposed) =>
  !actual || !proposed ? null : ((actual - proposed) / proposed) * 100;

// ── Year filter pills ─────────────────────────────────────────────────────────

const YearFilterPills = ({ selectedYear, onChange, availableYears }) => (
  <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 flex-wrap">
    <button
      type="button" onClick={() => onChange('all')}
      className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
        selectedYear === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      All time
    </button>
    {availableYears.map((yr) => (
      <button
        key={yr} type="button" onClick={() => onChange(yr)}
        className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
          selectedYear === yr ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {yr}+
      </button>
    ))}
  </div>
);

// ── Analysis helpers ──────────────────────────────────────────────────────────

const buildRetainerAnalysis = (components) => {
  if (!components.length) return null;
  const months     = [...new Set(components.map((c) => c.month))].sort();
  const monthCount = months.length;
  const totalRevenue = components.reduce((s, c) => s + (c.revenue || 0), 0);
  const totalHours   = components.reduce((s, c) => s + (c.hours  || 0), 0);
  const totalCost    = components.reduce((s, c) => s + (c.cost   || 0), 0);
  const billedMonths = months.filter((m) =>
    components.filter((c) => c.month === m).reduce((s, c) => s + (c.revenue || 0), 0) > 0
  );
  const billedMonthCount = billedMonths.length || monthCount;
  const avgRevenue = totalRevenue / billedMonthCount;
  const avgHours   = totalHours > 0 ? totalHours / monthCount : null;
  const avgCost    = totalCost  / billedMonthCount;
  const avgMargin  = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue) * 100 : null;

  const roleMap = {};
  components.forEach((c) => {
    const role = c.employee_role || 'Unknown';
    if (!roleMap[role]) roleMap[role] = { role, hours: 0, cost: 0, employees: new Set() };
    roleMap[role].hours += c.hours || 0;
    roleMap[role].cost  += c.cost  || 0;
    if (c.employee_name) roleMap[role].employees.add(c.employee_name);
  });
  const roles = Object.values(roleMap)
    .map((r) => ({ ...r, avgHours: r.hours / monthCount, avgCost: r.cost / monthCount,
      avgRate: r.hours > 0 ? r.cost / r.hours : 0, employeeCount: r.employees.size }))
    .sort((a, b) => b.avgHours - a.avgHours);

  const monthlyBreakdown = months.map((month) => {
    const mc = components.filter((c) => c.month === month);
    return {
      month,
      revenue:   mc.reduce((s, c) => s + (c.revenue || 0), 0),
      hours:     mc.reduce((s, c) => s + (c.hours   || 0), 0),
      cost:      mc.reduce((s, c) => s + (c.cost    || 0), 0),
      employees: mc.filter((c) => (c.hours || 0) > 0).sort((a, b) => (b.hours || 0) - (a.hours || 0)),
    };
  }).reverse();

  return { months, monthCount, totalRevenue, totalHours, totalCost,
    avgRevenue, avgHours, avgCost, avgMargin, roles, monthlyBreakdown };
};

const buildProjectAnalysis = (components) => {
  if (!components.length) return null;
  const totalRevenue = components.reduce((s, c) => s + (c.revenue || 0), 0);
  const totalHours   = components.reduce((s, c) => s + (c.hours   || 0), 0);
  const totalCost    = components.reduce((s, c) => s + (c.cost    || 0), 0);
  const margin       = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue) * 100 : 0;
  const roleMap = {};
  components.forEach((c) => {
    const role = c.employee_role || 'Unknown';
    if (!roleMap[role]) roleMap[role] = { role, hours: 0, cost: 0, employees: new Set() };
    roleMap[role].hours += c.hours || 0;
    roleMap[role].cost  += c.cost  || 0;
    if (c.employee_name) roleMap[role].employees.add(c.employee_name);
  });
  const roles = Object.values(roleMap)
    .map((r) => ({ ...r, avgRate: r.hours > 0 ? r.cost / r.hours : 0,
      employeeCount: r.employees.size, hoursPct: totalHours > 0 ? (r.hours / totalHours) * 100 : 0 }))
    .sort((a, b) => b.hours - a.hours);
  return { totalRevenue, totalHours, totalCost, margin, roles };
};

// ── Shared primitives ─────────────────────────────────────────────────────────

const MetricTile = ({ label, value, sub, color = 'slate', icon: Icon }) => {
  const colors = { slate: 'bg-slate-50 border-slate-200 text-slate-900', blue: 'bg-blue-50 border-blue-200 text-blue-900', green: 'bg-green-50 border-green-200 text-green-900', amber: 'bg-amber-50 border-amber-200 text-amber-900', red: 'bg-red-50 border-red-200 text-red-900', purple: 'bg-purple-50 border-purple-200 text-purple-900' };
  const iconColors = { slate: 'text-slate-400', blue: 'text-blue-400', green: 'text-green-500', amber: 'text-amber-500', red: 'text-red-500', purple: 'text-purple-500' };
  return (
    <div className={`rounded-lg border p-4 ${colors[color]}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider opacity-60 mb-1">{label}</p>
          <p className="text-xl font-bold">{value}</p>
          {sub && <p className="text-xs mt-1 opacity-60">{sub}</p>}
        </div>
        {Icon && <Icon className={`w-5 h-5 flex-shrink-0 ${iconColors[color]}`} />}
      </div>
    </div>
  );
};

const VariancePill = ({ pct }) => {
  if (pct == null) return null;
  const positive = pct >= 0;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${positive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      {positive ? <ArrowTrendingUpIcon className="w-3 h-3" /> : <ArrowTrendingDownIcon className="w-3 h-3" />}
      {positive ? '+' : ''}{pct.toFixed(1)}%
    </span>
  );
};

// ── Historical views ──────────────────────────────────────────────────────────

const RetainerHistoricalView = ({ components, engagement }) => {
  const [expandMonths, setExpandMonths] = useState(false);
  const analysis = useMemo(() => buildRetainerAnalysis(components), [components]);
  if (!analysis) return null;
  const proposedFee  = engagement?.proposed_fee || engagement?.estimated_value || 0;
  const revenueVsPct = variance(analysis.avgRevenue, proposedFee / analysis.monthCount);
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Monthly Averages — {analysis.monthCount} months of data</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricTile label="Avg Revenue / mo" value={fmt(analysis.avgRevenue)} sub={proposedFee ? `vs ${fmt(proposedFee / analysis.monthCount)} proposed` : null} color="green" icon={BanknotesIcon} />
          <MetricTile label="Avg Hours / mo" value={fmtHours(analysis.avgHours)} color="blue" icon={ClockIcon} />
          <MetricTile label="Avg Cost / mo" value={fmt(analysis.avgCost)} color="amber" icon={ChartBarIcon} />
          <MetricTile label="Avg Margin" value={fmtPct(analysis.avgMargin)} color={analysis.avgMargin == null ? 'slate' : analysis.avgMargin >= 20 ? 'green' : analysis.avgMargin >= 10 ? 'amber' : 'red'} icon={ArrowTrendingUpIcon} />
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Role Breakdown (monthly averages)</p>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600">Role</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600">Avg Hrs/mo</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600">Avg Rate</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600">Avg Cost/mo</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 hidden sm:table-cell">People</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {analysis.roles.map((role) => (
                <tr key={role.role} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{role.role}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{role.avgHours.toFixed(1)}</td>
                  <td className="px-4 py-3 text-right text-slate-500">{fmt(role.avgRate)}/hr</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmt(role.avgCost)}</td>
                  <td className="px-4 py-3 text-right text-slate-400 hidden sm:table-cell">{role.employeeCount}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 bg-slate-50">
              <tr>
                <td className="px-4 py-3 font-semibold text-slate-900">Total / mo</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{analysis.avgHours != null ? analysis.avgHours.toFixed(1) : '—'}</td>
                <td className="px-4 py-3 text-right text-slate-400">—</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmt(analysis.avgCost)}</td>
                <td className="px-4 py-3 hidden sm:table-cell" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      <div>
        <button onClick={() => setExpandMonths((v) => !v)} className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors">
          <CalendarIcon className="w-3.5 h-3.5" />
          {expandMonths ? 'Hide' : 'Show'} monthly breakdown ({analysis.monthCount} months)
        </button>
        {expandMonths && (
          <div className="mt-3 space-y-3">
            {analysis.monthlyBreakdown.map((m) => (
              <div key={m.month} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-sm font-semibold text-slate-700">{new Date(m.month).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</span>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <span>{fmt(m.revenue)}</span><span>{m.hours.toFixed(1)} hrs</span>
                    <VariancePill pct={variance(m.revenue, analysis.avgRevenue)} />
                  </div>
                </div>
                <table className="w-full text-xs">
                  <thead><tr className="border-b border-slate-100"><th className="px-4 py-2 text-left text-slate-500 font-medium">Employee</th><th className="px-4 py-2 text-left text-slate-500 font-medium">Role</th><th className="px-4 py-2 text-right text-slate-500 font-medium">Hours</th><th className="px-4 py-2 text-right text-slate-500 font-medium">Rate</th><th className="px-4 py-2 text-right text-slate-500 font-medium">Cost</th></tr></thead>
                  <tbody className="divide-y divide-slate-50">
                    {m.employees.map((emp, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-4 py-2 text-slate-800 font-medium">{emp.employee_name || '—'}</td>
                        <td className="px-4 py-2 text-slate-500">{emp.employee_role || '—'}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{(emp.hours || 0).toFixed(1)}</td>
                        <td className="px-4 py-2 text-right text-slate-500">{emp.rate ? `${fmt(emp.rate)}/hr` : '—'}</td>
                        <td className="px-4 py-2 text-right text-slate-700">{fmt(emp.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ProjectHistoricalView = ({ components, engagement }) => {
  const analysis = useMemo(() => buildProjectAnalysis(components), [components]);
  if (!analysis) return null;
  const proposedFee    = engagement?.proposed_fee || engagement?.estimated_value || 0;
  const estimatedHours = engagement?.estimated_hours || 0;
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Full Project Totals</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricTile label="Actual Revenue" value={fmt(analysis.totalRevenue)} sub={proposedFee ? `Proposed: ${fmt(proposedFee)}` : null} color="green" icon={BanknotesIcon} />
          <MetricTile label="Total Hours" value={fmtHours(analysis.totalHours)} sub={estimatedHours ? `Est: ${fmtHours(estimatedHours)}` : null} color="blue" icon={ClockIcon} />
          <MetricTile label="Total Cost" value={fmt(analysis.totalCost)} color="amber" icon={ChartBarIcon} />
          <MetricTile label="Profit Margin" value={fmtPct(analysis.margin)} color={analysis.margin == null ? 'slate' : analysis.margin >= 20 ? 'green' : analysis.margin >= 10 ? 'amber' : 'red'} icon={ArrowTrendingUpIcon} />
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Role Breakdown (full project)</p>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600">Role</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600">Hours</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600">% of Total</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600">Avg Rate</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600">Total Cost</th>
                <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 hidden sm:table-cell">People</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {analysis.roles.map((role) => (
                <tr key={role.role} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{role.role}</td>
                  <td className="px-4 py-3 text-right text-slate-700">{role.hours.toFixed(1)}</td>
                  <td className="px-4 py-3 text-right"><div className="flex items-center justify-end gap-2"><span className="text-slate-500">{role.hoursPct.toFixed(1)}%</span><div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(role.hoursPct, 100)}%` }} /></div></div></td>
                  <td className="px-4 py-3 text-right text-slate-500">{fmt(role.avgRate)}/hr</td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmt(role.cost)}</td>
                  <td className="px-4 py-3 text-right text-slate-400 hidden sm:table-cell">{role.employeeCount}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 border-slate-200 bg-slate-50">
              <tr>
                <td className="px-4 py-3 font-semibold text-slate-900">Total</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{analysis.totalHours.toFixed(1)}</td>
                <td className="px-4 py-3 text-right text-slate-400">100%</td>
                <td className="px-4 py-3 text-right text-slate-400">—</td>
                <td className="px-4 py-3 text-right font-semibold text-slate-900">{fmt(analysis.totalCost)}</td>
                <td className="px-4 py-3 hidden sm:table-cell" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

// ── Estimate views ────────────────────────────────────────────────────────────

const RetainerEstimateView = ({ components }) => {
  const analysis = useMemo(() => buildRetainerAnalysis(components), [components]);
  if (!analysis) return null;
  return (
    <div className="space-y-4">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-700">Based on <span className="font-semibold">{analysis.monthCount} months</span> of actuals. Rates are not shown here — apply current rates when building your bid.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Average Hours per Month by Role</p>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500">Role</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500">Avg Hrs / mo</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500">% of Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {analysis.roles.map((role) => {
              const pct = analysis.avgHours > 0 ? (role.avgHours / analysis.avgHours) * 100 : 0;
              return (
                <tr key={role.role} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{role.role}</td>
                  <td className="px-4 py-3 text-right font-semibold text-blue-700">{role.avgHours.toFixed(1)}</td>
                  <td className="px-4 py-3 text-right"><div className="flex items-center justify-end gap-2"><span className="text-slate-400 text-xs">{pct.toFixed(1)}%</span><div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} /></div></div></td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50">
            <tr>
              <td className="px-4 py-3 font-semibold text-slate-900">Total / mo</td>
              <td className="px-4 py-3 text-right font-bold text-blue-800">{analysis.avgHours != null ? analysis.avgHours.toFixed(1) : '—'}</td>
              <td className="px-4 py-3 text-right text-slate-400">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-slate-400 text-center">Apply your current role rates in Step 4 to calculate cost and margin.</p>
    </div>
  );
};

const ProjectEstimateView = ({ components }) => {
  const analysis = useMemo(() => buildProjectAnalysis(components), [components]);
  if (!analysis) return null;
  return (
    <div className="space-y-4">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-700">Total hours across the full project. Rates are not shown here — apply current rates when building your bid.</p>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-200">
          <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Total Hours by Role (Full Project)</p>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-slate-100">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-500">Role</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500">Total Hours</th>
              <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-500">% of Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {analysis.roles.map((role) => (
              <tr key={role.role} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{role.role}</td>
                <td className="px-4 py-3 text-right font-semibold text-blue-700">{role.hours.toFixed(1)}</td>
                <td className="px-4 py-3 text-right"><div className="flex items-center justify-end gap-2"><span className="text-slate-400 text-xs">{role.hoursPct.toFixed(1)}%</span><div className="w-16 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-blue-500 rounded-full" style={{ width: `${Math.min(role.hoursPct, 100)}%` }} /></div></div></td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t-2 border-slate-200 bg-slate-50">
            <tr>
              <td className="px-4 py-3 font-semibold text-slate-900">Total</td>
              <td className="px-4 py-3 text-right font-bold text-blue-800">{analysis.totalHours.toFixed(1)}</td>
              <td className="px-4 py-3 text-right text-slate-400">100%</td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p className="text-xs text-slate-400 text-center">Apply your current role rates in Step 4 to calculate cost and margin.</p>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const EngagementBidIntelligence = forwardRef(({ engagement }, ref) => {
  const [activeTab,    setActiveTab]    = useState('estimate');
  const [selectedYear, setSelectedYear] = useState('all');

  const engagementId = engagement?.id || engagement?.engagement_id;
  const isRetainer   = engagement?.engagement_type?.toLowerCase() === 'retainer';

  const { data: rawData, isLoading, isError } = useBidEngagementComponents({
    engagementId,
    enabled: !!engagementId,
  });

  // Deduplicated full set
  const allComponents = useMemo(() => {
    const raw = rawData?.components || [];
    if (!raw.length) return [];
    const seen = {};
    raw.forEach((c) => {
      const key = `${c.month}-${c.employee_name}-${c.employee_role}-${c.product_service}`;
      if (!seen[key] || c.created_at > seen[key].created_at) seen[key] = c;
    });
    return Object.values(seen).filter((c) => (c.hours || 0) > 0);
  }, [rawData]);

  // Years available in data
  const availableYears = useMemo(() => {
    const years = new Set(allComponents.map((c) => c.month?.slice(0, 4)).filter(Boolean));
    return [...years].sort();
  }, [allComponents]);

  // Year-filtered components — exposed to parent via ref
  const filteredComponents = useMemo(() => {
    if (!selectedYear || selectedYear === 'all') return allComponents;
    return allComponents.filter((c) => (c.month || '').slice(0, 4) >= selectedYear);
  }, [allComponents, selectedYear]);

  // Parent calls ref.current.getFilteredComponents() at "Use as Template" time
  useImperativeHandle(ref, () => ({
    getFilteredComponents: () => filteredComponents,
  }), [filteredComponents]);

  if (isLoading) return (
    <div className="p-6 flex items-center gap-3 text-sm text-slate-500">
      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      Loading bid intelligence...
    </div>
  );

  if (isError) return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
      Failed to load actuals data for this engagement.
    </div>
  );

  if (!allComponents.length) return (
    <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg text-center">
      <UserGroupIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" />
      <p className="text-sm font-medium text-slate-600">No actuals data available</p>
      <p className="text-xs text-slate-400 mt-1">Sync actuals from QuickBooks to see the breakdown</p>
    </div>
  );

  const tabs = [
    { id: 'estimate',   label: 'Use for Estimate', icon: CalculatorIcon, description: 'Hours by role only — apply current rates in Step 4' },
    { id: 'historical', label: 'Historical Detail', icon: TableCellsIcon, description: 'Full actuals with rates, cost, and employee breakdown' },
  ];

  const periodLabel = selectedYear === 'all'
    ? `All time · ${filteredComponents.length} records`
    : `${selectedYear}+ · ${filteredComponents.length} records`;

  return (
    <div className="space-y-4">

      {/* Type badge + period */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${isRetainer ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
          {isRetainer ? '📅 Retainer — monthly averages' : '📋 Project — full totals'}
        </span>
        <span className="text-xs text-slate-400">{periodLabel}</span>
      </div>

      {/* Year filter — only shown when data spans multiple years */}
      {availableYears.length > 1 && (
        <div>
          <p className="text-xs text-slate-500 mb-1.5">Filter period for estimate:</p>
          <YearFilterPills
            selectedYear={selectedYear}
            onChange={setSelectedYear}
            availableYears={availableYears}
          />
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-1 p-1 bg-slate-100 rounded-lg">
        {tabs.map((tab) => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold transition-all ${activeTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <tab.icon className="w-3.5 h-3.5" />{tab.label}
          </button>
        ))}
      </div>

      <p className="text-xs text-slate-400 -mt-1">{tabs.find((t) => t.id === activeTab)?.description}</p>

      {/* Content — uses filteredComponents */}
      {filteredComponents.length === 0 ? (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          No data for the selected period. Try "All time" or a different year.
        </div>
      ) : activeTab === 'estimate' ? (
        isRetainer
          ? <RetainerEstimateView components={filteredComponents} />
          : <ProjectEstimateView  components={filteredComponents} />
      ) : (
        isRetainer
          ? <RetainerHistoricalView components={filteredComponents} engagement={engagement} />
          : <ProjectHistoricalView  components={filteredComponents} engagement={engagement} />
      )}
    </div>
  );
});

EngagementBidIntelligence.displayName = 'EngagementBidIntelligence';
export default EngagementBidIntelligence;